import { GraphqlQueryResult } from "@graphql/client";
import { QueryError } from "@graphql/errorStore";
import { merge } from "@utils/nativeUtils";
import { SavingsQueryResult } from "@utils/types";

const combineData = (queryResults: GraphqlQueryResult<SavingsQueryResult>[]) =>
  queryResults
    .map((item) => item.data)
    // Each query returns a fragment of the overview shape; merge them back into
    // the full query shape the overview consumes.
    .reduce<SavingsQueryResult>((acc, cv) => merge(acc, (cv ?? {}) as Partial<SavingsQueryResult>), {});

// Stale-while-revalidate: a query only counts as "loading" while it has no data
// yet. Once cached data is present, a background refetch must not flip the
// overview back to its skeleton.
const combineLoading = (queryResults: GraphqlQueryResult<SavingsQueryResult>[]) =>
  queryResults.some((value) => value.loading && !value.data);

const combineError = (queryResults: GraphqlQueryResult<SavingsQueryResult>[]): QueryError | undefined => {
  // Only GraphQL errors surface a combined error. A transient network failure during a
  // background refetch must NOT blank the overview — the cached data stays rendered
  // (stale-while-revalidate). The executor carries GraphQL errors on a QueryError
  // (network-only failures throw and carry no `graphQLErrors`).
  const graphQLErrors = queryResults.flatMap((item) => {
    const { error } = item ?? {};
    return error instanceof QueryError ? error.graphQLErrors : [];
  });

  if (graphQLErrors.length === 0) {
    return undefined;
  }

  return new QueryError("Multiple queries failed!", graphQLErrors);
};

export interface CombinedQueryResult<T> {
  data: T;
  loading: boolean;
  error?: QueryError;
}

export const combineQueryResults = (
  queryResults: GraphqlQueryResult<SavingsQueryResult>[]
): CombinedQueryResult<SavingsQueryResult> => {
  const data = combineData(queryResults);
  const loading = combineLoading(queryResults);
  const error = combineError(queryResults);

  return {
    data,
    loading,
    error
  };
};
